import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { useTeachers } from "../../contexts/TeachersContext";
import { useCourses } from "../../contexts/CoursesContext";
import { CreateTeacherData, Teacher } from "../../api/teachers";
import { Loader2, Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../../components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";

interface AddProfessorProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    professorToEdit?: Teacher | null;
}

const AddProfessor: React.FC<AddProfessorProps> = ({
    open,
    onOpenChange,
    professorToEdit,
}) => {
    const { addTeacher, updateTeacher } = useTeachers();
    const { courses, refreshCourses } = useCourses();
    const { toast } = useToast();

    const [formData, setFormData] = useState<CreateTeacherData>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        specialization: "",
        courses: [],
    });

    const [loading, setLoading] = useState(false);
    const [openCombobox, setOpenCombobox] = useState(false);

    useEffect(() => {
        if (open) {
            refreshCourses();
        }
    }, [open, refreshCourses]);

    // Debug log to verify course data
    console.log("AddProfessor Courses:", courses);

    useEffect(() => {
        if (professorToEdit) {
            setFormData({
                firstName: professorToEdit.firstName,
                lastName: professorToEdit.lastName,
                email: professorToEdit.email,
                phone: professorToEdit.phone,
                specialization: professorToEdit.specialization || "",
                courses: professorToEdit.courses?.map(c => c.id) || [],
            });
        } else {
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                specialization: "",
                courses: [],
            });
        }
    }, [professorToEdit, open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const toggleCourse = (courseId: string) => {
        setFormData(prev => {
            const currentCourses = prev.courses || [];
            if (currentCourses.includes(courseId)) {
                return { ...prev, courses: currentCourses.filter(id => id !== courseId) };
            } else {
                return { ...prev, courses: [...currentCourses, courseId] };
            }
        });
    };

    const removeCourse = (courseId: string) => {
        setFormData(prev => ({
            ...prev,
            courses: (prev.courses || []).filter(id => id !== courseId)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (professorToEdit) {
                await updateTeacher(professorToEdit.id, formData);
                toast({
                    title: "Succès",
                    description: "Professeur modifié avec succès",
                });
            } else {
                await addTeacher(formData);
                toast({
                    title: "Succès",
                    description: "Professeur ajouté avec succès",
                });
            }
            onOpenChange(false);
        } catch (error: any) {
            toast({
                title: "Erreur",
                description: error.message || "Une erreur est survenue",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] overflow-visible">
                <DialogHeader>
                    <DialogTitle>
                        {professorToEdit ? "Modifier le professeur" : "Ajouter un professeur"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">Prénom <span className="text-red-500">*</span></Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Nom <span className="text-red-500">*</span></Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone">Téléphone <span className="text-red-500">*</span></Label>
                            <Input
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="specialization">Spécialisation <span className="text-red-500">*</span></Label>
                            <Input
                                id="specialization"
                                name="specialization"
                                value={formData.specialization}
                                onChange={handleChange}
                                placeholder="Ex: Cardiologie, Mathématiques..."
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Cours enseignés</Label>
                            <Popover open={openCombobox} onOpenChange={setOpenCombobox} modal={true}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCombobox}
                                        className="w-full justify-between"
                                        type="button"
                                    >
                                        {formData.courses && formData.courses.length > 0
                                            ? `${formData.courses.length} cours sélectionné(s)`
                                            : "Sélectionner des cours..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[450px] p-0 z-[200]" align="start">
                                    <Command>
                                        <CommandInput placeholder="Rechercher un cours..." />
                                        <CommandList>
                                            <CommandEmpty>Aucun cours trouvé.</CommandEmpty>
                                            <CommandGroup>
                                                {courses
                                                    .filter(course => !course.teacher || (professorToEdit && course.teacher.id === professorToEdit.id))
                                                    .map((course) => (
                                                        <CommandItem
                                                            key={course.id}
                                                            value={`${course.code} ${course.name}`} // Improve search capability
                                                            onSelect={() => {
                                                                toggleCourse(course.id);
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    formData.courses?.includes(course.id)
                                                                        ? "opacity-100"
                                                                        : "opacity-0"
                                                                )}
                                                            />
                                                            <span className="font-medium mr-2">{course.code}</span>
                                                            <span className="text-muted-foreground truncate">{course.name}</span>
                                                        </CommandItem>
                                                    ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>

                            {/* Selected Courses Badges */}
                            {formData.courses && formData.courses.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {formData.courses.map((courseId) => {
                                        const course = courses.find(c => c.id === courseId);
                                        if (!course) return null;
                                        return (
                                            <Badge key={courseId} variant="secondary" className="pl-2 pr-1 py-1">
                                                {course.code}
                                                <button
                                                    type="button"
                                                    onClick={() => removeCourse(courseId)}
                                                    className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            Annuler
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {professorToEdit ? "Modifier" : "Ajouter"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddProfessor;
